import type {
    HolisticSummaryCategory,
    SpendAccountCategory,
} from '@fruitful/types';
import { router } from 'expo-router';
import React, { useState } from 'react';
import {
    CheckIcon,
    FullScreenSpinner,
    Icon,
    ScreenContainer,
    useToastController,
    View,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { captureException } from '../../../logger/Logger';
import useCreateManualAccount, {
    type CreateManualAccountBody,
} from '../../hooks/useCreateManualAccount';
import useGetHolisticSummary from '../../hooks/useGetHolisticSummary';
import type { IManualAccount } from '../../types/ManualAccount';
import ManualAccountEditor from './ManualAccountEditor';

export interface Props {
    category?: HolisticSummaryCategory;
    type?: SpendAccountCategory;
}

export const CreateManualAccount = ({ category, type }: Props) => {
    const user = useAuthedUser();
    const toast = useToastController();

    const defaultCategory = category ?? undefined;
    const defaultType = type ?? undefined;

    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        refetch: refetchHolisticSummary,
        isLoading: isLoadingHolisticSummary,
    } = useGetHolisticSummary(false);

    const account = {
        name: undefined,
        institutionName: undefined,
        holisticCategory: defaultCategory,
        type: defaultType,
        amount: undefined,
        userId: user.id,
        membershipId: user.membership?.id ?? '',
    } satisfies Partial<IManualAccount>;

    const { mutate: createAccount, isPending: isCreatingManualAccount } =
        useCreateManualAccount();

    const onCreateAccount = (account: Partial<IManualAccount>) => {
        setIsSubmitting(true);
        const transformPartialAccount = (
            partialAccount: Partial<IManualAccount>,
        ): CreateManualAccountBody => {
            const {
                name,
                holisticCategory,
                type,
                amount,
                userId,
                membershipId,
            } = partialAccount;
            if (
                name == null ||
                holisticCategory == null ||
                amount == null ||
                userId == null ||
                membershipId == null
            ) {
                throw new Error('invalid manual account');
            }
            return {
                ...partialAccount,
                userId,
                membershipId,
                amount,
                holisticCategory: holisticCategory.toUpperCase(),
                type: type ?? null,
                name,
            };
        };

        createAccount(
            {
                body: {
                    ...transformPartialAccount(account),
                } satisfies CreateManualAccountBody, // convert
            },
            {
                onSuccess: (response: Partial<IManualAccount>) => {
                    setIsSubmitting(false);
                    void refetchHolisticSummary();
                    toast.show(`Success!`, {
                        icon: (
                            <View
                                backgroundColor="$green1000"
                                padding={16}
                                borderRadius={12}
                            >
                                <Icon
                                    icon={CheckIcon}
                                    size={20}
                                    color="$white"
                                />
                            </View>
                        ),
                        description: `${response.name} has been added to your accounts`,
                    });
                    if (router.canGoBack()) {
                        router.back();
                    } else {
                        router.navigate('/');
                    }
                },
                onError: (error) => {
                    setIsSubmitting(false);
                    toast.show('Something went wrong');
                    captureException(error);
                },
            },
        );
    };

    if (isCreatingManualAccount || isLoadingHolisticSummary || isSubmitting) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer title="Add new account" hasBackButton>
            <ManualAccountEditor
                submitAction={onCreateAccount}
                submitLabel="Add"
                account={account}
            />
        </ScreenContainer>
    );
};
